/**
 * LogicSetInfoModal Styles
 * PBI 266671 - Logic Set dynamic palette information
 *
 * Modal and tile styles. Diagram styles are in LogicSetDiagramReadOnly.style.js
 */

import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { Modal } from 'antd'

// Styled Modal with standard sizing pattern (top: 10vh, max-height: 80vh)
export const StyledModal = styled(Modal)`
  &&& {
    top: 10vh;
  }
  &&& {
    .ant-modal-content {
      display: flex;
      flex-direction: column;
      max-height: 80vh;
    }
    .ant-modal-body {
      padding: 0;
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }
  }
`

// Modal container
export const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  overflow: hidden;
`

export const ModalHeader = styled.div`
  display: flex;
  padding: 16px 25px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #e3e5e8;
  background: ${({ theme }) => theme.color.whitePrimary || '#FFF'};
`

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.headerColor};
`

export const ModalFooter = styled.div`
  display: flex;
  padding: 16px 25px;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  border-radius: 0 0 3px 3px;
  border-top: 1px solid #e3e5e8;
  background: ${({ theme }) => theme.color.whitePrimary || '#FFF'};
`

export const InstructionText = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #4a5565;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.15px;
`

export const ModalContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`

// Left side - Diagram
export const DiagramPanel = styled.div`
  display: flex;
  padding: 25px 25px 0 25px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
  align-self: stretch;
  background: #fbfbfb;
  overflow: auto;
`

// Right side - Tiles panel
export const TilesPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 280px;
  flex-shrink: 0;
  align-self: stretch;
  border-left: 1px solid #e3e5e8;
  background: #fff;
  overflow-y: auto;
`

// Individual tile styles
const tileBaseStyles = css`
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  position: relative;
  transition: background-color 0.15s ease;
`

const tileHoverStyles = ({ theme }) => css`
  &:hover {
    background-color: ${theme.color.lightGraySecondary || '#f5f5f5'};
  }
`

const tileSelectedStyles = ({ theme, isSelected }) =>
  isSelected &&
  css`
    background-color: ${theme.color.lightGraySecondary || '#f5f5f5'};

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 6px;
      background-color: ${theme.color.accentBlue || '#1890ff'};
    }
  `

export const Tile = styled.div`
  ${tileBaseStyles}
  ${tileHoverStyles}
  ${tileSelectedStyles}
`

export const TileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`

export const TileLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.headerColor};
`

export const TileIndex = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color.accentBlue || '#1890ff'};
  color: white;
  font-size: 11px;
  font-weight: 600;
`

export const TileDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.color.paragraphColor};
  white-space: pre-line;
`

// Link to open modal (used in LogicSetSimulator)
export const LearnHowLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  padding-top: 16px;
  padding-bottom: 16px;
  border-top: ${({ hideBorder }) =>
    hideBorder ? 'none' : '1px solid rgba(7, 7, 15, 0.4)'};
  border-bottom: ${({ hideBorder }) =>
    hideBorder ? 'none' : '1px solid rgba(7, 7, 15, 0.4)'};
  color: ${({ theme }) => theme.color.linkColor || '#1890ff'};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

// Info icon for Learn How link - now using custom SVG
export const LearnHowInfoIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
`
